import { usePathname } from 'next/navigation'
import { FC, memo, useCallback } from 'react'

import { Icon } from '~/src/components/generic/PhosphorIcons'

import { isRadiusUser, isUserSysadmin } from '~/helpers/auth'

import { useAuthenticatedUser } from '~/hooks/useAuthentication'

import { useShipmentTranslation } from '~/src/components/RyderShare/shared/terminology/useShipmentTranslation'
import { useTabTracking } from '~/app/(authenticated)/useTabTracking'
import { MixpanelEvent, track } from '~/clients/mixpanelClient'
import Tab from '~/src/components/Sidebar/Tab'

import { Page } from '~/app/types'

type Props = {
  pages: Readonly<Array<Page>>
  onOpenSettings?: () => void
}

const Sidebar: FC<Props> = ({ pages, onOpenSettings }) => {
  const { t } = useShipmentTranslation('common')

  const pathname = usePathname()
  const [user, logout] = useAuthenticatedUser()

  useTabTracking(pathname.slice(1))

  const hasRadiusAccess = isRadiusUser(user)
  const hasSysAdminAccess = isUserSysadmin(user)

  const handleOpenSettings = useCallback(() => {
    track(MixpanelEvent.UserSettingsOpen, { source: 'Sidebar' })
    onOpenSettings?.()
  }, [onOpenSettings])

  const handleLogout = useCallback(() => {
    logout()
  }, [logout])

  return (
    <div className="flex h-screen flex-col justify-center bg-gray-700 [grid-area:sidebar]">
      <div className="mt-auto">
        {pages
          .filter(({ isOnSidebar }) => isOnSidebar)
          .map(
            ({ path, title, subTitle, iconName }): JSX.Element => (
              <Tab
                aria-label={title}
                isSelected={pathname.startsWith(path)}
                key={path}
                path={path}
              >
                <div className="flex flex-col items-center space-y-2">
                  <Icon color="#fff" name={iconName} size="24" />
                  <p className="text-label-xs font-normal text-inverted">
                    {subTitle?.toLowerCase() === 'shipments'
                      ? t('common:shipment.sidebarLabel')
                      : t(`sidebar.${subTitle?.toLowerCase() ?? ''}`, {
                          defaultValue: subTitle,
                        })}
                  </p>
                </div>
              </Tab>
            )
          )}
      </div>
      <div className="mt-auto">
        {hasSysAdminAccess && (
          <Tab
            aria-label="Admin Control Center"
            isSelected={
              pathname === '/admin-control-center' ||
              pathname.startsWith('/admin-control-center')
            }
            key="admin-control-center"
            path="admin-control-center"
          >
            <div className="flex flex-col items-center space-y-2">
              <Icon color="#fff" name="UserGearIcon" size="24" />
              <p className="text-label-xs font-normal text-inverted">Admin</p>
            </div>
          </Tab>
        )}
        {hasRadiusAccess && (
          <Tab className="mt-auto" key="settings" onClick={handleOpenSettings}>
            <div className="flex flex-col items-center space-y-2">
              <Icon color="#fff" name="GearSixIcon" size="24" />
              <p className="text-label-xs font-normal text-inverted">Settings</p>
            </div>
          </Tab>
        )}
        <Tab
          className={hasRadiusAccess ? '' : 'mt-auto'}
          key="logout"
          onClick={handleLogout}
        >
          <div className="flex flex-col items-center space-y-2">
            <Icon color="#fff" name="SignOutIcon" size="24" />
            <p className="text-label-xs font-normal text-inverted">
              {hasRadiusAccess ? 'Log out' : 'Rydershare'}
            </p>
          </div>
        </Tab>
      </div>
    </div>
  )
}

export default memo(Sidebar)
